import * as React from "react"

import { cn } from "~/utils/helpers"

import { usePermissionGate, type PermissionGateOptions } from "./PermissionTooltip"

export type LinkButtonVariant = "neutral" | "primary" | "subtle"
export type LinkButtonSize = "sm" | "base"

// Pressed reuses hover and focus is Button's pill ring; Figma draws neither. `px-1 -mx-1` gives the
// ring room off the glyphs while the layout stays padding-free. The cap adds back the 8px the
// negative margins take off the parent's measure; `max-w-full` truncated every label by that much.
const BASE =
  "inline-flex min-w-0 max-w-[calc(100%+0.5rem)] items-center gap-1 -mx-1 whitespace-nowrap rounded-full px-1 transition-colors [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:pointer-events-none focus-visible:outline-none focus-visible:shadow-borders-focus"

// A disabled control gets none of these, so no hover rule can repaint it.
const VARIANT: Record<LinkButtonVariant, string> = {
  neutral: "text-fg-base hover:text-fg-subtle active:text-fg-subtle",
  primary: "text-fg-interactive hover:text-fg-interactive-hover active:text-fg-interactive-hover",
  subtle: "text-fg-subtle hover:text-fg-base active:text-fg-base",
}

const SIZE: Record<LinkButtonSize, string> = { sm: "label-sm", base: "label-base" }

const DISABLED_CLASSES = "text-fg-disabled cursor-not-allowed"

/** The classes LinkButton applies. */
export function linkButtonClasses({
  variant = "neutral",
  size = "sm",
  disabled = false,
}: {
  variant?: LinkButtonVariant
  size?: LinkButtonSize
  disabled?: boolean
}): string {
  return cn(BASE, SIZE[size], disabled ? DISABLED_CLASSES : ["cursor-pointer", VARIANT[variant]])
}

export interface LinkButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children">,
    PermissionGateOptions {
  variant?: LinkButtonVariant
  size?: LinkButtonSize
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  href?: string
  target?: React.HTMLAttributeAnchorTarget
  rel?: string
  children: React.ReactNode
}

/**
 * Text-only action: label with optional 16px leading / trailing icons, no fill or padding.
 * `variant` neutral (default) / primary / subtle, `size` sm (12px) / base (13px). Pass `href` for a
 * link, `onClick` for a button, `requires` to permission-gate it.
 * Canonical intent: {@link ../design-system/intents.ts LINK_BUTTON_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export const LinkButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, LinkButtonProps>(
  (
    {
      variant,
      size,
      leadingIcon,
      trailingIcon,
      href,
      target,
      rel,
      disabled,
      className,
      children,
      requires,
      permissionReason,
      allowedTitle,
      tooltipSide,
      tooltipClassName,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    const { blocked, gate } = usePermissionGate(
      { requires, permissionReason, allowedTitle, tooltipSide, tooltipClassName, wrapperClassName },
      "inline-flex",
    )
    const isDisabled = !!disabled || blocked
    const classes = cn(linkButtonClasses({ variant, size, disabled: isDisabled }), className)
    // Truncates only when a parent squeezes the link; icons keep their size.
    const content = (
      <>
        {leadingIcon}
        <span
          className="min-w-0 truncate"
          title={typeof children === "string" ? children : undefined}
        >
          {children}
        </span>
        {trailingIcon}
      </>
    )

    if (href !== undefined) {
      // A disabled link keeps its look but loses its href, so it can't be followed or focused.
      const { onClick, ...anchorProps } = props as React.AnchorHTMLAttributes<HTMLAnchorElement>
      return gate(
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={classes}
          {...anchorProps}
          {...(isDisabled
            ? { "aria-disabled": true, role: "link" }
            : {
                href,
                target,
                rel: rel ?? (target === "_blank" ? "noopener noreferrer" : undefined),
                onClick,
              })}
        >
          {content}
        </a>,
      )
    }

    return gate(
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        className={classes}
        disabled={isDisabled}
        {...props}
      >
        {content}
      </button>,
    )
  },
)

LinkButton.displayName = "LinkButton"
